// ============================================================================
// Hub CRM — crmadmin.dimoauto.com
//
// Rediseño de agosto de 2026: la barra de pestañas de arriba se sustituye por
// el menú lateral común de la suite (`src/ui/Sidebar.jsx`), plegable, con
// «Entrada de Leads» desplegándose en Email · Customer · Meta · Auto Leads.
// El dashboard de leads de siempre no desaparece: es la primera entrada del
// menú, «Resumen», con las mismas tarjetas y los mismos números.
//
// La lista de secciones (`SECCIONES`) es la ÚNICA fuente de verdad: la usan el
// menú lateral de escritorio y el selector de móvil. Antes eran dos listas
// escritas a mano que ya habían divergido.
//
// Con `?embed=1` (así lo abre el Centro de Aplicaciones) el menú esconde el
// logotipo: de la marca se encarga la cáscara y así no se ven dos.
// ============================================================================
import React, { useState, useEffect } from 'react';
import Dashboard from './components/Dashboard';
import CustomerLeadsApp from './apps/CustomerLeadsApp';
import AutoleadsApp from './apps/AutoleadsApp';
import CrmAdminApp from './apps/CrmAdminApp';
import CrmComercialesApp from './apps/CrmComercialesApp';
import EmailLeadsApp from './apps/EmailLeadsApp';
import Sidebar from './ui/Sidebar.jsx';
import { C } from './ui/theme.js';

const SUPABASE_URL = 'https://zkznupgyaorojxpydlly.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inprem51cGd5YW9yb2p4cHlkbGx5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjYwMjA5NTMsImV4cCI6MjA4MTU5Njk1M30.gKCpGxBeOCaaCRm10qQ_YpxXwTZQGbtTREsWrUG2RFY';

const EMBEBIDO = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('embed') === '1';

// ==================== MAPA DE SECCIONES ====================
// Fuente única para el menú lateral (escritorio) y el selector (móvil).
// `sub` agrupa las cuatro entradas de leads bajo un solo botón desplegable.
const SECCIONES = [
  { id: 'dashboard', label: 'Resumen', icon: 'resumen' },
  {
    id: 'entrada',
    label: 'Entrada de Leads',
    icon: 'entrada',
    sub: [
      { id: 'email-leads', label: 'Email' },
      { id: 'customer-leads', label: 'Customer' },
      { id: 'fb-meta-leads', label: 'Meta' },
      { id: 'autoleads', label: 'Auto Leads' },
    ],
  },
  { id: 'adriana', label: 'CallCenter / Gestión', icon: 'callcenter' },
  { id: 'crm-admin', label: 'CRM Admin', icon: 'admin' },
  { id: 'crm-comerciales', label: 'CRM Comerciales', icon: 'comerciales' },
];

/* Secciones que son apps completas y traen su propio menú lateral. Al entrar
   en ellas, el menú del hub se pliega solo a la tira de iconos: si no, se ven
   dos menús pegados comiéndose 400 px, y en el CallCenter eso dejaba la tabla
   sin sitio (petición 13-ago). Se declara fuera del componente para que sea
   siempre el mismo array y no dispare el efecto en cada render. */
const SECCIONES_CON_MENU_PROPIO = ['adriana', 'crm-admin'];

// Secciones tal y como las quiere el Sidebar (sin el campo `sub`).
const SECCIONES_MENU = SECCIONES.map(({ sub, ...s }) => s);
const SUBS = SECCIONES.reduce((acc, s) => (s.sub ? { ...acc, [s.id]: s.sub } : acc), {});

// Todas las hojas navegables, aplanadas: para el selector de móvil y para
// resolver a qué botón del menú pertenece la app que se está viendo.
const HOJAS = SECCIONES.flatMap((s) => (s.sub ? s.sub.map((t) => ({ ...t, padre: s.id })) : [{ ...s, padre: null }]));
const PADRE_DE = HOJAS.reduce((acc, h) => ({ ...acc, [h.id]: h.padre }), {});
const PRIMERA_SUB = SUBS.entrada[0].id;

const RecoveryModal = ({ accessToken, onClose }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    if (newPassword.length < 6) { setMessage('error:Minimo 6 caracteres'); return; }
    if (newPassword !== confirmPassword) { setMessage('error:Las contrasenas no coinciden'); return; }
    setLoading(true);
    try {
      const res = await fetch(SUPABASE_URL + '/auth/v1/user', {
        method: 'PUT',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': 'Bearer ' + accessToken,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      if (res.ok) {
        setMessage('success:Contrasena actualizada. Ya puedes iniciar sesion.');
        setTimeout(() => onClose(), 2500);
      } else {
        const err = await res.json();
        setMessage('error:' + (err.message || 'Error. El enlace puede haber expirado.'));
      }
    } catch { setMessage('error:Error de conexion.'); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,28,43,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 30, maxWidth: 360, width: '100%', boxShadow: '0 20px 60px rgba(15,42,70,0.35)' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>🔐</div>
          <h2 style={{ margin: '0 0 6px 0', fontSize: 19, fontWeight: 800, color: C.ink }}>Nueva contraseña</h2>
          <p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Introduce tu nueva contraseña para restablecer el acceso.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 5, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted }}>Nueva contraseña</label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres" required autoFocus
              style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: `1.5px solid ${C.line}`, fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', marginBottom: 5, fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: C.muted }}>Confirmar contraseña</label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              placeholder="Repite la contraseña" required
              style={{ width: '100%', padding: '10px 12px', borderRadius: 9, border: `1.5px solid ${C.line}`, fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' }} />
          </div>
          {message && (
            <div style={{
              padding: '10px 12px', borderRadius: 9, marginBottom: 14, fontSize: 12.5,
              background: message.startsWith('success:') ? `${C.verde}12` : `${C.rojo}12`,
              border: `1px solid ${message.startsWith('success:') ? C.verde : C.rojo}44`,
              color: message.startsWith('success:') ? C.verde : C.rojo,
            }}>
              {message.replace(/^(success:|error:)/, '')}
            </div>
          )}
          <button type="submit" disabled={loading} style={{ width: '100%', padding: 12, borderRadius: 10, border: 'none', background: loading ? C.gris : C.brand, color: '#fff', fontWeight: 800, fontSize: 14, fontFamily: 'inherit', cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Guardando…' : 'Guardar contraseña'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ==================== MÓVIL ====================
// En el teléfono el menú lateral no cabe: se mantiene el selector de siempre,
// pero alimentado por SECCIONES para que no vuelva a divergir del de escritorio.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

const SelectorMovil = ({ activeApp, setActiveApp }) => (
  <header style={{
    background: '#fff', borderBottom: `1px solid ${C.line}`, padding: '10px 14px',
    display: 'flex', alignItems: 'center', gap: 10, position: 'sticky', top: 0, zIndex: 1000,
  }}>
    {!EMBEBIDO && <img src="/logotipo.png" alt="Dimoauto" style={{ height: 20, width: 'auto', flexShrink: 0 }} />}
    <select
      value={activeApp}
      onChange={(e) => setActiveApp(e.target.value)}
      style={{
        flex: 1, padding: '9px 12px', borderRadius: 10, border: `1px solid ${C.line}`,
        background: '#fff', color: C.brand, fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {SECCIONES.map((s) => (s.sub
        ? <optgroup key={s.id} label={s.label}>
            {s.sub.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </optgroup>
        : <option key={s.id} value={s.id}>{s.label}</option>
      ))}
    </select>
  </header>
);

// Embebe la app externa Meta Leads dentro del Hub para conservar el menú.
// En móvil el iframe necesita altura en píxeles explícita (no %) para que
// iOS/Android permitan scroll interno.
const MetaLeadsFrame = () => {
  const mobile = useIsMobile();
  const frameHeight = mobile ? 'calc(100vh - 52px)' : '100%';
  return (
    <iframe
      src="https://meta-live.dimoauto.com/"
      title="Meta Leads"
      scrolling="yes"
      style={{ width: '100%', height: frameHeight, minHeight: mobile ? 'calc(100vh - 52px)' : '100%', border: 'none', display: 'block', WebkitOverflowScrolling: 'touch' }}
      allow="clipboard-read; clipboard-write"
    />
  );
};

// Panel Recepción de Leads (Adriana) — app independiente embebida en el Hub.
const AdrianaFrame = () => {
  const mobile = useIsMobile();
  const frameHeight = mobile ? 'calc(100vh - 52px)' : '100%';
  return (
    <iframe
      src="https://n3rcwo40ek77ptxeg5a372to.204.168.215.119.sslip.io/" /* TEMPORAL: URL Coolify por defecto hasta crear DNS recepcion.dimoauto.com */
      title="Adriana — Recepción de Leads"
      scrolling="yes"
      style={{ width: '100%', height: frameHeight, minHeight: mobile ? 'calc(100vh - 52px)' : '100%', border: 'none', display: 'block', WebkitOverflowScrolling: 'touch' }}
      allow="clipboard-read; clipboard-write"
    />
  );
};

function App() {
  const isMobile = useIsMobile();
  const [activeApp, setActiveApp] = useState('dashboard');
  const [recoveryToken, setRecoveryToken] = useState(null);
  const [recovering, setRecovering] = useState(false);

  useEffect(() => {
    const detectRecovery = async () => {
      const hash = window.location.hash;
      if (!hash || hash.length < 5) return;
      const params = new URLSearchParams(hash.substring(1));

      // Formato nuevo (template personalizado): #recovery=TOKEN_HASH
      const tokenHash = params.get('recovery');
      if (tokenHash) {
        setRecovering(true);
        window.history.replaceState(null, '', window.location.pathname);
        try {
          const res = await fetch(SUPABASE_URL + '/auth/v1/verify', {
            method: 'POST',
            headers: { 'apikey': SUPABASE_ANON_KEY, 'Content-Type': 'application/json' },
            body: JSON.stringify({ token_hash: tokenHash, type: 'recovery' })
          });
          if (res.ok) {
            const data = await res.json();
            if (data.access_token) setRecoveryToken(data.access_token);
          }
        } catch (e) {}
        setRecovering(false);
        return;
      }

      // Formato legacy: #access_token=xxx&type=recovery
      const accessToken = params.get('access_token');
      const type = params.get('type');
      if (accessToken && type === 'recovery') {
        window.history.replaceState(null, '', window.location.pathname);
        setRecoveryToken(accessToken);
      }
    };
    detectRecovery();
  }, []);

  // Escucha el evento `openApp` emitido por Dashboard (tarjetas clicables).
  useEffect(() => {
    const handler = (e) => {
      const next = e?.detail;
      if (typeof next === 'string') setActiveApp(next);
    };
    window.addEventListener('openApp', handler);
    return () => window.removeEventListener('openApp', handler);
  }, []);

  const renderActiveApp = () => {
    switch (activeApp) {
      case 'dashboard': return <Dashboard />;
      case 'customer-leads': return <CustomerLeadsApp />;
      case 'fb-meta-leads': return <MetaLeadsFrame />;
      case 'autoleads': return <AutoleadsApp />;
      case 'adriana': return <AdrianaFrame />;
      case 'crm-admin': return <CrmAdminApp />;
      case 'crm-comerciales': return <CrmComercialesApp />;
      case 'email-leads': return <EmailLeadsApp />;
      default: return <Dashboard />;
    }
  };

  if (recovering) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
        <div style={{ textAlign: 'center', color: C.brand }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔐</div>
          <p style={{ fontSize: 15, fontWeight: 700 }}>Verificando enlace…</p>
        </div>
      </div>
    );
  }

  // MÓVIL — selector desplegable, mismo mapa de secciones que el escritorio
  if (isMobile) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: C.bg }}>
        {recoveryToken && <RecoveryModal accessToken={recoveryToken} onClose={() => setRecoveryToken(null)} />}
        <SelectorMovil activeApp={activeApp} setActiveApp={setActiveApp} />
        <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {renderActiveApp()}
        </div>
      </div>
    );
  }

  // ESCRITORIO — menú lateral plegable
  // El botón «Entrada de Leads» se ve activo cuando lo que hay abierto es
  // cualquiera de sus cuatro hojas.
  const seccionActiva = PADRE_DE[activeApp] || activeApp;
  const subActiva = PADRE_DE[activeApp] ? activeApp : PRIMERA_SUB;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: C.bg }}>
      {recoveryToken && <RecoveryModal accessToken={recoveryToken} onClose={() => setRecoveryToken(null)} />}

      <Sidebar
        sections={SECCIONES_MENU}
        active={seccionActiva}
        onSelect={(id) => setActiveApp(id === 'entrada' ? subActiva : id)}
        subs={SUBS}
        subActive={{ entrada: subActiva }}
        onSelectSub={(sec, t) => setActiveApp(t)}
        marca={!EMBEBIDO}
        claveMemoria="crm-sidebar-colapsado"
        autoPlegarEn={SECCIONES_CON_MENU_PROPIO}
      />

      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'auto' }}>
        {renderActiveApp()}
      </main>
    </div>
  );
}

export default App;
